import React from 'react';

import { TRACKING_OPT_OUT } from '@/utils/analytics/trackingOptOut';
import { Icon, Skeleton } from '../../../../components';
import type { StatsDelta } from '../../../../mock-data/types';
import { Delta } from '../../../stats/components';
import {
  List,
  Row,
  Line,
  Names,
  Name,
  SubName,
  Figures,
  ValueLine,
  Value,
  Note,
  Track,
  Fill,
  Empty,
  SubLink,
  SubLinkText,
} from './RankedList.styles';

export type RankedItem = {
  key: string;
  /** What the row is, in plain words (already translated). */
  name: string;
  /** A quieter second line under the name (e.g. the page's address). */
  sub?: string;
  /** Opens what the second line names (the live page) in a new tab. */
  href?: string;
  /** The row's main figure, formatted. */
  value: string;
  /** A quieter figure under the value. */
  note?: string;
  /** Bar length, 0..1 of the largest row. */
  share: number;
  /** The row's figure against the period before (null: not compared). */
  change?: StatsDelta | null;
};

export type RankedListProps = {
  items: RankedItem[];
  loading?: boolean;
  error?: boolean;
  /** Shown when there is nothing to rank (already translated). */
  emptyText: string;
  errorText: string;
  /** Placeholder rows while loading. */
  placeholderRows?: number;
};

/** A ranked list with a share bar per row: most visited pages, devices, addresses. */
export const RankedList = ({
  items,
  loading = false,
  error = false,
  emptyText,
  errorText,
  placeholderRows = 4,
}: RankedListProps): React.ReactElement => {
  if (loading) {
    return (
      <List aria-busy="true">
        {Array.from({ length: placeholderRows }, (_, i) => (
          <Row key={i}>
            <Line>
              <Skeleton width="45%" height={16} radius={6} />
              <Skeleton width={56} height={16} radius={6} />
            </Line>
            <Skeleton block height={6} radius={999} />
          </Row>
        ))}
      </List>
    );
  }
  if (error) return <Empty text={errorText} tag="span" raw />;
  if (items.length === 0) return <Empty text={emptyText} tag="span" raw />;
  return (
    <List {...TRACKING_OPT_OUT}>
      {items.map((item) => (
        <Row key={item.key}>
          <Line>
            <Names>
              <Name text={item.name} tag="span" raw title={item.name} />
              {item.sub && item.href ? (
                <SubLink href={item.href} target="_blank" rel="noopener noreferrer" title={item.href}>
                  <SubLinkText>{item.sub}</SubLinkText>
                  <Icon name="external-link" size={11} />
                </SubLink>
              ) : item.sub ? (
                <SubName text={item.sub} tag="span" raw title={item.sub} />
              ) : null}
            </Names>
            <Figures>
              <ValueLine>
                {item.change ? <Delta delta={item.change} size={10} /> : null}
                <Value text={item.value} tag="span" raw />
              </ValueLine>
              {item.note ? <Note text={item.note} tag="span" raw /> : null}
            </Figures>
          </Line>
          <Track aria-hidden="true">
            <Fill $share={item.share} />
          </Track>
        </Row>
      ))}
    </List>
  );
};

export default RankedList;
